import React, { useMemo, useState } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Check, Feather } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { endpoints } from "@/lib/api";
import SoulHero from "@/components/soul/SoulHero";
import SoulBreadcrumb from "@/components/soul/SoulBreadcrumb";
import { findPractice } from "@/pages/soul/soulContent";

/**
 * InnerPractice — the generic seven-step practice page.
 *
 * Progression: Arrive → Understand → Recognize → Go Deeper →
 * Inner Listening → Practice → Integrate.
 *
 * Reflections + integration answers stay local to the component. The
 * user can save the whole reflection as a single Journal entry via the
 * existing /api/journal endpoint — nothing is duplicated in a new store.
 */
export default function InnerPractice() {
  const { slug } = useParams();
  const nav = useNavigate();
  const practice = useMemo(() => findPractice(slug), [slug]);
  const [deeper, setDeeper] = useState({}); // { qIndex: text }
  const [listening, setListening] = useState("");
  const [integrate, setIntegrate] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  if (!practice) return <Navigate to="/soul/inner-exploration" replace />;

  const hasAnyReflection =
    Object.values(deeper).some((v) => (v || "").trim()) ||
    listening.trim() ||
    integrate.trim();

  const composeJournal = () => {
    const parts = [];
    parts.push(`Soul · Inner Exploration · ${practice.title}`);
    parts.push("");
    const deeperEntries = practice.deeper
      .map((q, i) => ({ q, a: (deeper[i] || "").trim() }))
      .filter((x) => x.a);
    if (deeperEntries.length) {
      parts.push("— Go Deeper —");
      for (const { q, a } of deeperEntries) {
        parts.push(q);
        parts.push(a);
        parts.push("");
      }
    }
    if (listening.trim()) {
      parts.push("— Inner Listening —");
      parts.push(practice.listening);
      parts.push(listening.trim());
      parts.push("");
    }
    if (integrate.trim()) {
      parts.push("— What I want to carry forward —");
      parts.push(integrate.trim());
    }
    return parts.join("\n");
  };

  const saveToJournal = async () => {
    if (!hasAnyReflection || saving) return;
    setSaving(true);
    try {
      await endpoints.createJournal({
        prompt: `Soul · ${practice.title}`,
        content: composeJournal(),
        mood_tag: "reflect",
        entry_type: "Soul Work Reflection",
      });
      setSaved(true);
      toast.success("Held in your Journal.", {
        action: {
          label: "Open Journal",
          onClick: () => nav("/journal"),
        },
      });
    } catch {
      toast.error("Couldn't save just yet — try again in a moment.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-12" data-testid={`soul-practice-page-${practice.slug}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <SoulBreadcrumb
          items={[
            { label: "Rituals", to: "/rituals" },
            { label: "Soul", to: "/soul" },
            { label: "Inner Exploration", to: "/soul/inner-exploration" },
            { label: practice.title },
          ]}
        />
        <button
          type="button"
          onClick={() => nav("/soul/inner-exploration")}
          data-testid="soul-practice-back"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--copper))] transition-colors"
        >
          <ArrowLeft size={12} /> Back to Inner Exploration
        </button>
      </div>

      <SoulHero
        emblem=""
        eyebrow={`Soul · ${practice.eyebrow}`}
        title={practice.title}
        supporting={practice.tagline}
        image={practice.image}
        testid="soul-practice-hero"
      />

      {/* 1. Arrive */}
      <StepBlock numeral="I" label="Arrive" testid="soul-step-arrive">
        <p className="font-display italic text-xl md:text-2xl leading-snug text-[hsl(var(--fg))]/85 max-w-3xl">
          {practice.arrive}
        </p>
      </StepBlock>

      {/* 2. Understand */}
      <StepBlock numeral="II" label="Understand" testid="soul-step-understand">
        <div className="space-y-4 max-w-3xl">
          {practice.understand.map((p, i) => (
            <p key={i} className="text-[15.5px] leading-relaxed text-[hsl(var(--fg))]/85">
              {p}
            </p>
          ))}
        </div>
      </StepBlock>

      {/* 3. Recognize */}
      <StepBlock numeral="III" label="Recognize" testid="soul-step-recognize">
        <ul className="space-y-4 max-w-3xl">
          {practice.recognize.map((line, i) => (
            <li
              key={i}
              className="pl-5 border-l-2 border-[hsl(var(--copper))]/40 text-[15.5px] leading-relaxed text-[hsl(var(--fg))]/85"
            >
              {line}
            </li>
          ))}
        </ul>
      </StepBlock>

      {/* 4. Go Deeper */}
      <StepBlock numeral="IV" label="Go Deeper" testid="soul-step-deeper">
        <p className="text-sm text-[hsl(var(--muted-foreground))] max-w-3xl">
          Answer only what wants to be answered. Nothing here is required.
        </p>
        <div className="mt-6 space-y-6 max-w-3xl">
          {practice.deeper.map((q, i) => (
            <div key={i} data-testid={`soul-deeper-${i}`}>
              <label
                htmlFor={`soul-deeper-input-${i}`}
                className="block text-[15px] text-[hsl(var(--fg))] leading-relaxed"
              >
                {q}
              </label>
              <Textarea
                id={`soul-deeper-input-${i}`}
                value={deeper[i] || ""}
                onChange={(e) => setDeeper((prev) => ({ ...prev, [i]: e.target.value }))}
                rows={3}
                placeholder="Answer only what wants to be answered…"
                className="mt-2 bg-transparent border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--copper))]"
                data-testid={`soul-deeper-input-${i}`}
              />
            </div>
          ))}
        </div>
      </StepBlock>

      {/* 5. Inner Listening */}
      <StepBlock numeral="V" label="Inner Listening" testid="soul-step-listening">
        <div className="max-w-3xl">
          <p className="font-display italic text-xl md:text-2xl leading-snug text-[hsl(var(--clay))]">
            {practice.listening}
          </p>
          <Textarea
            value={listening}
            onChange={(e) => setListening(e.target.value)}
            rows={4}
            placeholder="What arrives when you stop trying to explain it…"
            className="mt-5 bg-transparent border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--copper))]"
            data-testid="soul-listening-input"
          />
        </div>
      </StepBlock>

      {/* 6. The Practice */}
      <StepBlock numeral="VI" label="The Practice" testid="soul-step-practice">
        <div
          className="p-7 md:p-9 rounded-[20px] border hairline bg-gradient-to-br from-[hsl(var(--sand))]/50 to-[hsl(var(--paper))] max-w-3xl"
          data-testid="soul-practice-block"
        >
          <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--copper))]">
            {practice.practice.kind === "visualization" && "A quiet visualization"}
            {practice.practice.kind === "writing" && "A written practice"}
            {practice.practice.kind === "spoken" && "A spoken practice"}
            {practice.practice.kind === "reflection" && "A quiet reflection"}
          </div>
          <h3 className="font-display text-2xl md:text-3xl mt-2 tracking-tight italic">
            {practice.practice.title}
          </h3>
          <ol className="mt-6 space-y-3">
            {practice.practice.steps.map((s, i) => (
              <li
                key={i}
                className="flex gap-4 text-[15px] leading-relaxed text-[hsl(var(--fg))]/85"
              >
                <span className="shrink-0 mt-0.5 w-6 h-6 rounded-full bg-[hsl(var(--copper))]/12 text-[hsl(var(--copper))] text-xs flex items-center justify-center font-medium">
                  {i + 1}
                </span>
                <span>{s}</span>
              </li>
            ))}
          </ol>
          {practice.practice.closing && (
            <p className="mt-6 pt-6 border-t border-[hsl(var(--line))] font-display italic text-lg text-[hsl(var(--clay))] leading-snug">
              {practice.practice.closing}
            </p>
          )}
        </div>
      </StepBlock>

      {/* 7. Integrate */}
      <StepBlock numeral="VII" label="Integrate" testid="soul-step-integrate">
        <div className="max-w-3xl">
          <label htmlFor="soul-integrate-input" className="block text-[15px] text-[hsl(var(--fg))] leading-relaxed">
            {practice.integrate}
          </label>
          <Textarea
            id="soul-integrate-input"
            value={integrate}
            onChange={(e) => setIntegrate(e.target.value)}
            rows={4}
            placeholder="One small thing to carry with you…"
            className="mt-3 bg-transparent border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--copper))]"
            data-testid="soul-integrate-input"
          />

          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <Button
              type="button"
              onClick={saveToJournal}
              disabled={!hasAnyReflection || saving || saved}
              data-testid="soul-save-to-journal"
              className="rounded-full bg-[hsl(var(--copper))] hover:bg-[hsl(var(--copper))]/90 text-[hsl(var(--paper))]"
            >
              {saved ? (
                <>
                  <Check size={14} className="mr-1.5" /> Held in Journal
                </>
              ) : (
                <>
                  <Feather size={14} className="mr-1.5" /> {saving ? "Holding…" : "Save to Journal"}
                </>
              )}
            </Button>
            {saved && (
              <button
                type="button"
                onClick={() => nav("/journal")}
                className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--copper))] hover:opacity-80"
                data-testid="soul-open-journal"
              >
                Open Journal →
              </button>
            )}
          </div>
        </div>
      </StepBlock>
    </div>
  );
}

function StepBlock({ numeral, label, children, testid }) {
  return (
    <section
      className="reveal reveal-1"
      data-testid={testid}
    >
      <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--copper))] inline-flex items-center gap-3">
        <span className="tabular-nums">{numeral}</span>
        <span aria-hidden className="w-8 h-px bg-[hsl(var(--copper))]/40" />
        <span>{label}</span>
      </div>
      <div className="mt-6">{children}</div>
    </section>
  );
}
